import { MusicCard } from '@components/Cards';
import { CarouselLeftArrowIcon } from '@components/Icons/CarouselLeftArrowIcon';
import { CarouselRightArrowIcon } from '@components/Icons/CarouselRightArrowIcon';
import { MusicIcon2 } from '@components/Icons/MusicIcon2';
import { RightArrowicon } from '@components/Icons/RightArrowIcon';
import NapsterSpinner from '@components/NapsterSpinner';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import useAlbumData from '@hooks/useAlbumData';
import { useSwipeCarousel } from '@hooks/useSwipeCarousel';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Features, placeHolderData } from '../../../../config';
import { Album } from '../../../../types';

export const HomeIndianMusic = () => {
  const { t } = useTranslation();
  const { setActivePlaylist, addRecentTrack } = useMusicState();
  const { setActivePopup } = useCommonState();
  const { addFavouriteItem, loadAlbumById, loadIndianMusicAlbumsPaginated } =
    useAlbumData();

  const [albums, setAlbums] = useState<Album[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [displayError, setDisplayError] = useState<string | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [loadingAlbumId, setLoadingAlbumId] = useState<string | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  const { containerRef } = useSwipeCarousel({
    threshold: 50,
  });

  useEffect(() => {
    const fetchAlbums = async () => {
      try {
        setIsLoading(true);
        setDisplayError(null);
        const result = await loadIndianMusicAlbumsPaginated(20, 0);
        setAlbums(result?.items || []);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load Indian music';
        setDisplayError(errorMessage);
        console.error('Error fetching Indian music:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAlbums();
  }, [loadIndianMusicAlbumsPaginated]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const scrollLeft = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;

    setCanScrollLeft(scrollLeft > 25);
    setCanScrollRight(scrollLeft < maxScroll - 25);
  };

  const openMusicPlayerPage = useCallback(
    async (album: Album) => {
      try {
        setLoadingAlbumId(album.id);
        const albumData = await loadAlbumById(album.id);

        if (!albumData || !albumData.tracks || albumData.tracks.length === 0) {
          console.warn(
            'No tracks found in album:',
            albumData?.albumName || album.id,
          );
          setLoadingAlbumId(null);
          return;
        }

        const contributor = albumData.contributors?.[0];
        const firstTrack = albumData.tracks[0];

        const newActivePlaylist = {
          currentIndex: 0,
          album: albumData,
        };

        const recentTrack = {
          track: {
            name: firstTrack.name,
            url: firstTrack.url,
            trackIndex: 1,
          },
          contributor: {
            id: albumData.id,
            givenName: contributor?.givenName || 'Unknown Artist',
            thumbnailUrl:
              contributor?.thumbnailUrl || placeHolderData.contributorThumbnail,
          },
          albumArtUrl:
            albumData.albumArtUrl ||
            contributor?.thumbnailUrl ||
            placeHolderData.albumCover,
          timestamp: Date.now(),
        };
        addRecentTrack(recentTrack);

        setActivePlaylist(newActivePlaylist);
        setActivePopup('musicplayer');
        setLoadingAlbumId(null);
      } catch (error) {
        console.error('Error loading album:', error);
        setLoadingAlbumId(null);
      }
    },
    [loadAlbumById, setActivePlaylist, setActivePopup, addRecentTrack],
  );

  const handleCardClick = useCallback(
    (album: Album) => {
      openMusicPlayerPage(album);
    },
    [openMusicPlayerPage],
  );

  if (!Features.enableIndianMusic) {
    return null;
  }

  return (
    <div id="indian-music-for-you" className=" w-full mb-10">
      <div className="flex w-full items-center justify-between mb-4 font-[NapsterSans] px-4 sm:px-6">
        <div className="flex gap-[6px] sm:gap-[8px] items-center hover-transition">
          <div className="flex">
            <MusicIcon2 />
          </div>
          <Link
            className="flex gap-[6px] sm:gap-[8px] items-center text-[15px] sm:text-[18px] text-white hover:text-[#fcfcfc] transition-all duration-500 ease-in-out font-bold leading-[normal] cursor-pointer"
            to="/indian-music">
            {t('home.indianMusic')}
            <span className="hidden sm:inline">
              <RightArrowicon />
            </span>
          </Link>
        </div>
        <Link
          to="/indian-music"
          className="text-xs sm:text-sm text-white hover:text-[#fcfcfc] hover:underline transition-all duration-300 ease-in-out cursor-pointer">
          {t('home.showAll')}
        </Link>
      </div>

      {isLoading && albums.length === 0 && (
        <div className="flex gap-[16px] pb-[30px] h-full overflow-x-auto snap-x snap-mandatory scroll-smooth hide-scrollbar scrollbar-hide select-none scroll-pl-4 sm:scroll-pl-6">
          <div className="flex-shrink-0 w-4 sm:w-6"></div>
          {[...Array(4)].map((_, index) => (
            <div
              key={`loading-${index}`}
              className="flex-shrink-0 w-[371px] snap-start">
              <div className="relative w-full h-[520px] rounded-[16px] bg-gray-800 animate-pulse overflow-hidden shadow-[0_4px_16px_0_rgba(0,0,0,0.40)]">
                <div className="absolute top-0 left-0 w-full h-full bg-gray-700"></div>

                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_34.69%,rgba(0,0,0,0.9)_100%)] z-[99]" />

                <div className="absolute top-[16px] left-[16px] z-[100] w-[78px] h-[40px] bg-gray-600 rounded-full"></div>

                <div className="absolute bottom-[120px] left-[20px] z-[50] overflow-hidden w-[172px] h-[132px]">
                  <div className="absolute top-0 left-[35px] w-[73px] h-[73px] md:w-[132px] md:h-[132px] rounded-full bg-gray-600 z-[5]"></div>
                  <div className="absolute left-0 top-0 w-[73px] h-[73px] md:w-[132px] md:h-[132px] bg-gray-600 rounded-[8px] z-[10]"></div>
                </div>

                <div className="absolute z-[100] bottom-0 left-0 p-5 flex flex-col gap-3 w-full">
                  <div className="h-[28px] bg-gray-600 rounded w-4/5"></div>

                  <div className="flex items-center gap-2">
                    <div className="w-[56px] h-[56px] rounded-full bg-gray-600 flex-shrink-0"></div>
                    <div className="h-[18px] bg-gray-600 rounded w-32"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div className="flex-shrink-0 w-4 sm:w-6"></div>
        </div>
      )}

      {displayError && !isLoading && (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <div className="text-red-400 mb-4">
              <svg
                className="w-12 h-12 mx-auto mb-4"
                fill="currentColor"
                viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-lg font-medium">{t('home.failedToLoadMusic')}</p>
              <p className="text-sm text-red-300 mt-1">{displayError}</p>
            </div>
          </div>
        </div>
      )}

      {!displayError && !isLoading && albums.length === 0 && (
        <div className="flex gap-[16px] pb-[30px] h-[520px] overflow-x-auto snap-x snap-mandatory scroll-smooth hide-scrollbar scrollbar-hide select-none scroll-pl-4 sm:scroll-pl-6">
          <div className="flex-shrink-0 w-4 sm:w-6"></div>
          <div className="flex items-center justify-center w-full h-full">
            <div className="text-center text-white/60">
              <div className="mb-4">
                <svg
                  className="w-16 h-16 mx-auto mb-4 text-white/40"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
                  />
                </svg>
              </div>
              <p className="text-lg font-medium mb-2">{t('home.noMusicAvailable')}</p>
              <p className="text-sm text-white/40">
                {t('home.noTrendingMusic')}
              </p>
            </div>
          </div>
          <div className="flex-shrink-0 w-4 sm:w-6"></div>
        </div>
      )}

      {!displayError && albums.length > 0 && (
        <div className="relative w-full group">
          {canScrollLeft && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const container = containerRef.current || carouselRef.current;
                if (container) {
                  container.scrollBy({
                    left: -400,
                    behavior: 'smooth',
                  });
                }
              }}
              className="absolute left-9 top-1/2 -translate-y-1/2 z-[300] hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-black/60 hover:bg-black/80 transition-all duration-200 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto"
              aria-label={t('aria.previousSlide')}>
              <CarouselLeftArrowIcon />
            </button>
          )}

          {canScrollRight && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const container = containerRef.current || carouselRef.current;
                if (container) {
                  container.scrollBy({
                    left: 400,
                    behavior: 'smooth',
                  });
                }
              }}
              className="absolute right-9 top-1/2 -translate-y-1/2 z-[300] hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-black/60 hover:bg-black/80 transition-all duration-200 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto"
              aria-label={t('aria.nextSlide')}>
              <CarouselRightArrowIcon />
            </button>
          )}

          <div
            ref={(el) => {
              carouselRef.current = el;
              containerRef.current = el;
            }}
            className="flex gap-[16px] pb-[30px] h-full overflow-x-auto snap-x snap-mandatory scroll-smooth hide-scrollbar scrollbar-hide select-none scroll-pl-4 sm:scroll-pl-6"
            onScroll={handleScroll}>
            <div className="flex-shrink-0 w-4 sm:w-6"></div>
            {albums.map((album, index) => {
              const contributor = album.contributors[0];
              const albumId = album.id || album.albumName || index.toString();
              const isLoading = loadingAlbumId === album.id;
              const posterUrl =
                album.videoMobilePreviewUrl ||
                contributor?.videoMobilePreviewUrl ||
                contributor?.thumbnailUrl;

              return (
                <div
                  key={`album-${albumId}`}
                  className="flex-shrink-0 w-[371px] snap-start relative">
                  <MusicCard
                    album={album}
                    contributor={contributor}
                    videoUrl={
                      album.videoMobileUrl || contributor?.videoMobileUrl
                    }
                    posterUrl={posterUrl}
                    albumArtUrl={album.albumArtUrl}
                    albumName={album.albumName}
                    likes={album.likes}
                    isLiked={album.isLiked}
                    onClick={() => handleCardClick(album)}
                    onFavoriteClick={(e) => {
                      e.stopPropagation();
                      addFavouriteItem('album', album.id, album.isLiked);
                    }}
                    showPlayButton={false}
                    showFavoriteButton={false}
                    showLikesCount={true}
                  />
                  {isLoading && (
                    <div className="absolute z-[100] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                      <NapsterSpinner />
                    </div>
                  )}
                </div>
              );
            })}

            <div className="flex-shrink-0 w-4 sm:w-6"></div>
          </div>
        </div>
      )}
    </div>
  );
};
